import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowRight, Flag, FlagCheckered, Trophy, Target, PersonSimpleRun, Bicycle, Check, CheckCircle, Trash } from '@phosphor-icons/react';
import ProfileDialog from '../components/profile/ProfileDialog';
import TimePicker from '../components/TimePicker';
import GoalGuardrailModal from '../components/GoalGuardrailModal';
import RaceTypePickerModal from '../components/RaceTypePickerModal';
import IntensityBadge from '../components/ui/IntensityBadge';
import CoachNote from '../components/ui/CoachNote';
import { validateUserGoal } from '../utils/goalValidator';
import authFetch from '../utils/authFetch';
import { CYCLING_ENABLED } from '../config/features';
import { planRegenRequest } from '../utils/planRegenRequest';
import { showToast } from '../utils/toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';

// Date guard — race target dates must be today or in the future.
// Empty / invalid string returns false (let required-field check handle that).
function isPastRaceDate(iso) {
  if (!iso) return false;
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
}

function Races({ onBack, userId }) {
  const [races, setRaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingRace, setEditingRace] = useState(null);
  // In-app confirm (Graphite, 2026-10-27) in place of the browser's window.confirm — same question, same outcome.
  const [confirmDialog, setConfirmDialog] = useState(null);
  const confirmResolve = useRef(null);
  const closeConfirm = useCallback((answer) => {
    const resolve = confirmResolve.current;
    confirmResolve.current = null;
    setConfirmDialog(null);
    if (resolve) resolve(!!answer);
  }, []);
  const askConfirm = (title, message, confirmLabel) => new Promise((resolve) => {
    confirmResolve.current = resolve;
    setConfirmDialog({ kind: 'confirm', title, message, confirmLabel });
  });

  // Profile data for goal races
  const [runningProfile, setRunningProfile] = useState(null);
  const [cyclingProfile, setCyclingProfile] = useState(null);

  // Personal bests state
  const [raceResults, setRaceResults] = useState([]);
  const [showAddResult, setShowAddResult] = useState(false);
  const [savingResult, setSavingResult] = useState(false);
  const [resultPickerOpen, setResultPickerOpen] = useState(false);
  const [addRacePickerOpen, setAddRacePickerOpen] = useState(false);
  const [editRacePickerOpen, setEditRacePickerOpen] = useState(false);
  const [newResult, setNewResult] = useState({
    distance_category: '10k',
    race_name: '',
    race_date: '',
    finish_time_display: '',
    notes: ''
  });

  // 🚴🏃 Sport type tab
  const [activeTab, setActiveTab] = useState('running'); // 'running', 'cycling', or 'pbs'

  // Goal guardrail modal state — opened from the save flow when target/volume
  // mismatch is detected. `mode` says which save flow to resume after the user
  // decides ('add' or 'edit').
  const [guardrailState, setGuardrailState] = useState(null); // {result, mode} | null

  // ── Race-change regenerate flow (2026-05-31) ──────────────────
  // After any race add / edit / delete that touches plan-shaping
  // fields (distance / date / target_time / is_goal_race), pop a
  // toast asking whether to rebuild the active plan. Mirrors the
  // Profile.jsx flow — same /profile/can-regenerate 24h gate, same
  // global plan-regen-start/done banner via App.js.
  const [regenerateToast, setRegenerateToast] = useState(null);
  // Race-impact modal (coach spec 2026-06-05). Drives the
  // race-saved/edited/deleted flow when the backend's deterministic
  // 21-day-window check says the change actually affects the active
  // plan. Shape: { summary, raceSport, raceDate, cost, remaining,
  // daily_limit, target_sports }.
  const [raceImpactModal, setRaceImpactModal] = useState(null);

  // Open the race-impact modal with quota transparency. Reads cached
  // message-usage from localStorage (populated by App.js
  // loadMessageUsage) so the modal can render exact 'cost vs remaining'
  // numbers without an extra fetch. The server-side guard in
  // /weekly-plan/regenerate-all is the source of truth — this is just
  // UI hinting.
  const _openRaceImpactModal = ({ summary, raceSport, raceDate, isGoalRace = false }) => {
    const _activeSports = _getActiveSports();
    // Race change regen scope (2026-06-05 fix): BOTH active sports,
    // not just the sport of the race itself. Previously we regenerated
    // only the race's own sport on the assumption that 'the other
    // sport doesn't need a refresh unless the race is shared' — that
    // assumption was wrong. The cross-sport coordination block in the
    // prompt (ASYMMETRIC TAPER, SAME-DAY VOLUME CAP, hard-day overlap
    // rules) means a running race actively reshapes the cycling plan
    // and vice versa. Real-world symptom: user deleted the running
    // race 'בית שאן' from races, only the running plan regenerated,
    // and the cycling plan kept its "Light Taper for the בית שאן race"
    // intro for days afterwards. The single source of truth is now
    // _activeSports — if both sports are on, both get regenerated.
    const _targetSports = _activeSports.length ? _activeSports : ['running'];
    const _previewSports = _targetSports;
    const _cost = _previewSports.length * 4;
    let _remaining = null;
    let _dailyLimit = null;
    try {
      const _cached = localStorage.getItem(`message_usage_${userId}`);
      if (_cached) {
        const _u = JSON.parse(_cached);
        _remaining = typeof _u.remaining === 'number' ? _u.remaining : null;
        _dailyLimit = typeof _u.daily_limit === 'number' ? _u.daily_limit : null;
      }
    } catch (_) {}
    setRaceImpactModal({
      summary,
      raceSport,
      raceDate,
      isGoalRace,
      target_sports: _previewSports,
      cost: _cost,
      remaining: _remaining,
      daily_limit: _dailyLimit,
    });
  };

  // 'Update Now' CTA — hits the new batch endpoint so the heavy work
  // lives server-side (F5-safe) and runs only against the touched
  // sport (~2.7 min instead of ~5.5 min for the full matrix).
  const _confirmRaceImpactUpdate = async () => {
    const m = raceImpactModal;
    if (!m) return;
    const sports = m.target_sports || [m.raceSport || 'running'];
    const changedFields = [m.summary];
    setRaceImpactModal(null);
    try {
      const result = await planRegenRequest({
        url: `${API_BASE_URL}/weekly-plan/regenerate-all?user_id=${userId}`,
        body: {
          sports,
          weeks: ['current', 'next', 'week_plus2', 'week_plus3'],
          triggered_by_user: true,
          force: true,
        },
        pollUserId: userId,
        changedFields,
      });
      if (!result.success) {
        if (result.error === 'insufficient_quota') {
          showToast(
            `⚠️ אין מספיק קריאות יומיות (נדרשות ${result.required}, נותרו ${result.remaining}). המכסה מתאפסת בחצות.`,
            'error', 6000,
          );
        } else {
          showToast('⚠️ עדכון התכנית נכשל — אפשר לנסות שוב מאוחר יותר', 'error', 5000);
        }
        return;
      }
      const _sportsLabel = sports.length > 1
        ? 'ריצה + רכיבה'
        : sports[0] === 'cycling' ? 'רכיבה' : 'ריצה';
      // Clear stale plan cache so WeeklyPlan fetches fresh on next mount.
      // plansUpdated tells WeeklyPlan to re-fetch immediately if already mounted.
      try { localStorage.removeItem(`plans_${userId}`); } catch (_) {}
      window.dispatchEvent(new CustomEvent('plansUpdated', { detail: { source: 'race_impact' } }));
      showToast(`✅ תכנית ${_sportsLabel} עודכנה — כל 4 השבועות הפעילים!`, 'success', 4500);
    } catch (e) {
      console.error('Race-impact regen failed:', e);
      showToast('⚠️ עדכון התכנית נכשל — אפשר לנסות שוב מאוחר יותר', 'error', 5000);
    }
  };

  // Race type picker modal — opens after "Add race" click instead of the
  // legacy is_goal_race checkbox. User picks Goal Race vs Tune-up; closing
  // the modal commits as Tune-up (the safe-for-volume default).
  const [raceTypePicker, setRaceTypePicker] = useState(null); // {mode: 'add'|'edit'} | null
  
  // Form state
  const [newRace, setNewRace] = useState({
    race_name: '',
    race_date: '',
    distance: '10K',
    target_time: '',
    is_goal_race: false,
    sport_type: 'running'
  });

  // Distance options by sport type. Running list is limited to the 4
  // distances the chat / plan generator can target with a meaningful
  // pace strategy (Race Week Protocol + tempo tables). Ultra distances
  // (50K, 100K) and the catch-all "אחר" were removed because the
  // tooling has no race-specific logic for them.
  const runningDistanceOptions = [
    '5K',
    '10K',
    'חצי מרתון',
    'מרתון',
  ];

  const cyclingDistanceOptions = [
    '30 ק"מ',
    '50 ק"מ',
    '70 ק"מ',
    '100 ק"מ',
    '150 ק"מ',
    '200 ק"מ',
    'Gran Fondo',
    'אחר'
  ];

  // Get distance options based on selected sport type in form
  const getDistanceOptions = (sportType) => {
    return sportType === 'cycling' ? cyclingDistanceOptions : runningDistanceOptions;
  };

  const distanceKmFrom = (distLabel) => {
    const d = (distLabel || '').toLowerCase();
    if (d === '5k') return 5;
    if (d === '10k') return 10;
    if (d === '15k') return 15;
    // Check half marathon BEFORE marathon to avoid substring match
    if (d.includes('חצי מרתון') || d.includes('half') || d === '21.1') return 21.1;
    if (d.includes('מרתון') || d.includes('marathon') || d.includes('42')) return 42.2;
    if (d.includes('חצי') || d.includes('21')) return 21.1;
    const n = parseFloat(d);
    return n > 0 ? n : null;
  };

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Deep-link prefill from Chat: when the user clicked "✨ הוסף מרוץ חדש"
  // in chat, App.js stashed {race_name, race_date} in localStorage and
  // navigated here. Open the add-race modal with whatever fields we have —
  // race_name is the only requirement; date may be empty (user fills it,
  // along with distance + target_time, before saving).
  useEffect(() => {
    try {
      const raw = localStorage.getItem('races_prefill');
      if (!raw) return;
      const prefill = JSON.parse(raw);
      if (prefill && prefill.race_name) {
        setNewRace((prev) => ({
          ...prev,
          race_name: prefill.race_name,
          // Use the date if we have one, otherwise leave empty for the user
          race_date: prefill.race_date || prev.race_date || '',
        }));
        setShowAddForm(true);
      }
    } catch (e) {
      console.warn('races_prefill parse failed:', e);
    } finally {
      // One-shot — clear so the next visit doesn't auto-open.
      try { localStorage.removeItem('races_prefill'); } catch {}
    }
  }, []);

  // Handoff from Profile guardrail: when the user accepts
  // "סגור תוכנית ל-<distance>" in the goal-mismatch modal that was
  // triggered by THIS race (e.g. marathon scheduled with too-low km),
  // App.js stashed {raceId, suggestedDistance} and routed here.
  // Open the edit modal pre-filled with the suggested distance so
  // saving converts the marathon → half marathon (or whatever the
  // validator suggested) instead of changing profile.goal_distance.
  // Depends on [races] — we need the loaded list to find the row.
  useEffect(() => {
    if (!races || races.length === 0) return;
    let raw;
    try { raw = localStorage.getItem('pending_race_edit'); } catch { return; }
    if (!raw) return;
    try {
      const { raceId, suggestedDistance, ts } = JSON.parse(raw);
      // 60s TTL — older entries are stale (user navigated back, etc.)
      if (!ts || Date.now() - ts > 60_000) return;
      const target = races.find(r => String(r.id) === String(raceId));
      if (!target) return;
      // Map Hebrew label from the validator's suggestedDistance back
      // into the races-table distance encoding. The Races form keeps
      // distance as raw strings ('5K' / '10K' / 'חצי מרתון' / 'מרתון')
      // — same vocabulary as suggestedDistance, so no remap needed.
      setEditingRace({
        ...target,
        sport_type: target.sport_type || 'running',
        distance: suggestedDistance || target.distance,
      });
      setShowEditForm(true);
    } catch (e) {
      console.warn('pending_race_edit parse failed:', e);
    } finally {
      try { localStorage.removeItem('pending_race_edit'); } catch {}
    }
  }, [races]);

  // Load all data: races + profiles + race results
  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadRaces(),
        loadRunningProfile(),
        loadCyclingProfile(),
        loadRaceResults(),
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRaceResults = async () => {
    try {
      const res = await authFetch(`${API_BASE_URL}/race-results?user_id=${userId}`);
      if (res.ok) {
        const data = await res.json();
        setRaceResults(data.results || []);
      }
    } catch (e) {
      console.error('Error loading race results:', e);
    }
  };

  const handleAddResult = async () => {
    if (!newResult.race_name || !newResult.race_date || !newResult.finish_time_display) {
      alert('נא למלא שם מרוץ, תאריך וזמן סיום');
      return;
    }
    setSavingResult(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/race-results?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newResult),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (data.is_pb) alert('שיא אישי חדש!');
        await loadRaceResults();
        setShowAddResult(false);
        setNewResult({ distance_category: '10k', race_name: '', race_date: '', finish_time_display: '', notes: '' });
      } else {
        alert(data.detail || 'שגיאה בשמירה');
      }
    } catch (e) {
      alert('שגיאה בשמירה');
    } finally {
      setSavingResult(false);
    }
  };

  const handleDeleteResult = async (id) => {
    if (!(await askConfirm('למחוק את התוצאה?', 'התוצאה תימחק מרשימת הביצועים.', 'מחק'))) return;
    try {
      const res = await authFetch(`${API_BASE_URL}/race-results/${id}?user_id=${userId}`, { method: 'DELETE' });
      if (res.ok) await loadRaceResults();
    } catch (e) {
      console.error(e);
    }
  };

  const loadRaces = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/races?user_id=${userId}`);
      if (response.ok) {
        const data = await response.json();
        setRaces(data.races || []);
      }
    } catch (error) {
      console.error('Error loading races:', error);
    }
  };

  const loadRunningProfile = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/profile?user_id=${userId}`);
      if (response.ok) {
        const data = await response.json();
        setRunningProfile(data);
      }
    } catch (error) {
      console.error('Error loading running profile:', error);
    }
  };

  const loadCyclingProfile = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/profile/cycling/${userId}`);
      if (response.ok) {
        const data = await response.json();
        if (data && !data.error) {
          setCyclingProfile(data);
        }
      }
    } catch (error) {
      console.error('Error loading cycling profile:', error);
    }
  };

  // Merge races from table with goal races from profiles
  const getAllRaces = () => {
    let allRaces = [...races].map(r => ({ ...r, sport_type: r.sport_type || 'running' }));
    
    // Helper function to check if race already exists (by name)
    const raceExists = (name) => {
      if (!name) return true;
      const normalizedName = name.trim().toLowerCase();
      return allRaces.some(r => {
        const existingName = (r.race_name || r.name || '').trim().toLowerCase();
        return existingName === normalizedName;
      });
    };

    // 🏃 Add running goal race from profile if not already in races
    if (runningProfile?.goal_race && runningProfile?.goal_date) {
      if (!raceExists(runningProfile.goal_race)) {
        allRaces.push({
          id: 'running_goal_from_profile',
          race_name: runningProfile.goal_race,
          race_date: runningProfile.goal_date,
          distance: runningProfile.goal_distance || '',
          target_time: runningProfile.goal_time || '',
          is_goal_race: true,
          sport_type: 'running',
          from_profile: true
        });
      }
    }

    // 🚴 Add cycling goal race from profile if not already in races
    if (cyclingProfile?.cycling_goal_event && cyclingProfile?.cycling_goal_date) {
      if (!raceExists(cyclingProfile.cycling_goal_event)) {
        allRaces.push({
          id: 'cycling_goal_from_profile',
          race_name: cyclingProfile.cycling_goal_event,
          race_date: cyclingProfile.cycling_goal_date,
          distance: cyclingProfile.cycling_goal_distance || '',
          target_time: '',
          is_goal_race: true,
          sport_type: 'cycling',
          from_profile: true
        });
      }
    }

    return allRaces;
  };

  // Internal save — bypasses the guardrail. `overrideGoal` is true when the
  // user pressed "continue anyway" on a guardrail warning. `isGoalRaceArg`
  // is the explicit choice from the race-type picker modal (passed because
  // setNewRace is async and may not have settled yet by the time we save).
  const persistNewRace = async (overrideGoal = false, isGoalRaceArg) => {
    setSaving(true);
    try {
      const _isGoalRace = (typeof isGoalRaceArg === 'boolean') ? isGoalRaceArg : !!newRace.is_goal_race;
      const response = await authFetch(`${API_BASE_URL}/races`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          ...newRace,
          is_goal_race: _isGoalRace,
          override_goal: !!overrideGoal,
        })
      });

      const data = await response.json();
      if (response.ok && data.success) {
        const savedName = newRace.race_name || 'תחרות חדשה';
        const savedSport = newRace.sport_type;
        await loadRaces();
        setShowAddForm(false);
        setNewRace({
          race_name: '',
          race_date: '',
          distance: activeTab === 'cycling' ? '70 ק"מ' : '10K',
          target_time: '',
          is_goal_race: false,
          sport_type: activeTab
        });
        // Race-impact modal (coach spec 2026-06-05). Only prompt if
        // the backend's deterministic 21-day window check says this
        // race affects the active plan. Far-future races just save
        // silently and get picked up by a future regenerate when
        // the date enters the window.
        // Always bust cached plan so the next page load fetches fresh data.
        try { localStorage.removeItem(`plans_${userId}`); } catch (_) {}
        if (data.requires_plan_update) {
          _openRaceImpactModal({
            summary: `הוספת תחרות "${savedName}"`,
            raceSport: data.sport_type || savedSport || 'running',
            raceDate: newRace.race_date,
            isGoalRace: _isGoalRace,
          });
        } else {
          showToast(`✅ התחרות "${savedName}" נשמרה. התכנית תתעדכן אוטומטית כשהמרוץ יתקרב.`, 'success', 4000);
        }
      } else {
        alert((typeof data.detail === 'object' ? data.detail?.message : data.detail) || data.message || 'שגיאה בהוספת התחרות');
      }
    } catch (error) {
      alert('שגיאה בהוספת התחרות: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // After user picks Goal Race / Tune-up in the modal, finish the save:
  // run the goal guardrail (running only) and persist.
  const finalizeAddAfterTypePick = async (isGoalRace) => {
    setNewRace((p) => ({ ...p, is_goal_race: isGoalRace }));
    setRaceTypePicker(null);
    // Run the guardrail for ALL running races (goal AND tune-up). Earlier
    // it was gated to is_goal_race=true on the assumption that tune-ups
    // don't drive taper and therefore don't need volume/pace checks — but
    // the user pointed out that an unrealistic tune-up target (1:26 half
    // at 38 km/week) is just as injury-prone as the same goal race, and
    // pilot users were adding races as tune-ups and silently bypassing
    // every check. So now both types go through the guardrail.
    if (newRace.sport_type !== 'cycling') {
      const weeklyKm = parseFloat(runningProfile?.avg_weekly_km);
      const result = validateUserGoal(weeklyKm, newRace.distance, newRace.target_time);
      if (result.status !== 'ok') {
        setGuardrailState({ result, mode: 'add' });
        return;
      }
    }
    await persistNewRace(false, isGoalRace);
  };

  const finalizeEditAfterTypePick = async (isGoalRace) => {
    setEditingRace((p) => ({ ...p, is_goal_race: isGoalRace }));
    setRaceTypePicker(null);
    if (editingRace.sport_type !== 'cycling') {
      const weeklyKm = parseFloat(runningProfile?.avg_weekly_km);
      const result = validateUserGoal(weeklyKm, editingRace.distance, editingRace.target_time);
      if (result.status !== 'ok') {
        setGuardrailState({ result, mode: 'edit' });
        return;
      }
    }
    await persistEditRace(false, isGoalRace);
  };

  const handleAddRace = async () => {
    // Required: race_name + race_date + distance + target_time.
    // target_time is mandatory because the chat / plan-generator anchors all
    // pace tables (Race Week Protocol, tempo offsets, long-run progression)
    // on it, and the goal-guardrail validator needs it too.
    const missing = [];
    if (!newRace.race_name) missing.push('שם תחרות');
    if (!newRace.race_date) missing.push('תאריך');
    if (!newRace.distance) missing.push('מרחק');
    if (newRace.sport_type !== 'cycling' && !newRace.target_time) missing.push('זמן יעד');
    if (missing.length) {
      alert('נא למלא את השדות החובה:\n• ' + missing.join('\n• '));
      return;
    }
    if (isPastRaceDate(newRace.race_date)) {
      alert('⚠️ תאריך התחרות לא יכול להיות בעבר.\nעדכן את התאריך להיום או לעתיד.');
      return;
    }
    // Cycling: no race-type distinction yet — save directly as tune-up.
    if (newRace.sport_type === 'cycling') {
      await persistNewRace(false, false);
      return;
    }
    // Running: open the race type picker before saving.
    setRaceTypePicker({ mode: 'add' });
  };

  const handleEditRace = async () => {
    if (!editingRace) {
      alert('שגיאה: אין תחרות לעריכה');
      return;
    }
    const missing = [];
    if (!editingRace.race_name) missing.push('שם תחרות');
    if (!editingRace.race_date) missing.push('תאריך');
    if (!editingRace.distance) missing.push('מרחק');
    if (editingRace.sport_type !== 'cycling' && !editingRace.target_time) missing.push('זמן יעד');
    if (missing.length) {
      alert('נא למלא את השדות החובה:\n• ' + missing.join('\n• '));
      return;
    }
    if (isPastRaceDate(editingRace.race_date)) {
      alert('⚠️ תאריך התחרות לא יכול להיות בעבר.\nעדכן את התאריך להיום או לעתיד.');
      return;
    }
    if (editingRace.sport_type === 'cycling') {
      await persistEditRace(false, !!editingRace.is_goal_race);
      return;
    }
    setRaceTypePicker({ mode: 'edit' });
  };

  const persistEditRace = async (overrideGoal = false, isGoalRaceArg) => {
    setSaving(true);
    try {
      const _isGoalRace = (typeof isGoalRaceArg === 'boolean') ? isGoalRaceArg : !!editingRace.is_goal_race;
      const response = await authFetch(`${API_BASE_URL}/races/${editingRace.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,  // Required by RaceCreate model
          race_name: editingRace.race_name,
          race_date: editingRace.race_date,
          distance: editingRace.distance,
          target_time: editingRace.target_time,
          is_goal_race: _isGoalRace,
          sport_type: editingRace.sport_type,
          override_goal: !!overrideGoal,
        })
      });

      const data = await response.json();
      if (response.ok) {
        const editedName = editingRace.race_name || 'תחרות';
        const editedSport = editingRace.sport_type;
        await loadRaces();
        setShowEditForm(false);
        setEditingRace(null);
        // Always bust cached plan so the next page load fetches fresh data.
        try { localStorage.removeItem(`plans_${userId}`); } catch (_) {}
        if (data.requires_plan_update) {
          _openRaceImpactModal({
            summary: `עדכון תחרות "${editedName}"`,
            raceSport: data.sport_type || editingRace?.sport_type || 'running',
            raceDate: editingRace?.race_date,
            isGoalRace: _isGoalRace,
          });
        } else {
          showToast(`✅ התחרות "${editedName}" עודכנה. התכנית לא דורשת חידוש כרגע.`, 'success', 4000);
        }
      } else {
        alert((typeof data.detail === 'object' ? data.detail?.message : data.detail) || data.message || 'שגיאה בעדכון התחרות');
      }
    } catch (error) {
      console.error('Error updating race:', error);
      alert('שגיאה בעדכון התחרות');
    } finally {
      setSaving(false);
    }
  };

  // Trigger the regenerate toast after a successful race
  // add / edit / delete. The `summary` text shows in the toast so
  // the user sees WHICH action prompted the question.
  //
  // Rate limit kept (24h, server-side): the user can keep editing
  // and saving repeatedly. Each save asks. If they already used
  // their daily regenerate, the toast shows in 'blocked' mode with
  // 'ניתן לעדכן תכנית פעם ביום' — but the save itself is NEVER
  // blocked. User spec 2026-05-31.
  const triggerRegenerateToast = async (summary) => {
    try {
      const rateRes = await authFetch(`${API_BASE_URL}/profile/can-regenerate?user_id=${userId}`);
      const rate = await rateRes.json();
      if (rate.can_regenerate) {
        setRegenerateToast({ mode: 'ask', hoursRemaining: 0, changedFields: [summary] });
      } else {
        setRegenerateToast({ mode: 'blocked', hoursRemaining: rate.hours_remaining || 1, changedFields: [summary] });
      }
    } catch (e) {
      console.warn('can-regenerate check failed (allowing toast):', e);
      setRegenerateToast({ mode: 'ask', hoursRemaining: 0, changedFields: [summary] });
    }
  };

  // Which sports does this athlete have active? cyclingProfile.sports_active
  // is the canonical comma-separated list ('running' or 'running,cycling').
  // Running-only athletes won't have a cycling profile row populated, so
  // default to ['running'] when nothing was loaded yet.
  const _getActiveSports = () => {
    const raw = cyclingProfile?.sports_active || 'running';
    return String(raw).split(',').map(s => s.trim()).filter(Boolean);
  };

  // Yes-regenerate handler — sequential generate-v2 for current +
  // next week, FOR EVERY active sport. User spec: 'every change
  // must include both running and cycling'. Sequential not parallel:
  // 2 concurrent Gemini Pro calls OOMed Render's free tier earlier.
  // Dispatches plan-regen-start/done events so App.js's global
  // banner stays visible across screens during the ~30-60s job.
  const handleConfirmRegenerate = async () => {
    const changedFields = (regenerateToast && regenerateToast.changedFields) || [];
    setRegenerateToast({ mode: 'generating', changedFields });
    window.dispatchEvent(new CustomEvent('plan-regen-start', { detail: { changedFields } }));
    try {
      // Each call goes through planRegenRequest in silent mode (this
      // function owns the outer plan-regen-start/done events for the
      // batch). The helper transparently handles both sync (200 +
      // plan) and async (202 + job_id + polling) backends.
      for (const sport of _getActiveSports()) {
        for (const week_type of ['current', 'next']) {
          // eslint-disable-next-line no-await-in-loop
          const result = await planRegenRequest({
            url: `${API_BASE_URL}/weekly-plan/generate-v2?user_id=${userId}`,
            body: { week_type, sport_type: sport, triggered_by_user: true },
            pollUserId: userId,
            silent: true,
          });
          if (!result.success) throw new Error(`${result.error || 'fail'} for ${sport}/${week_type}`);
        }
      }
      setRegenerateToast({ mode: 'done', changedFields });
      setTimeout(() => setRegenerateToast(null), 3000);
      window.dispatchEvent(new CustomEvent('plan-regen-done', { detail: { success: true } }));
      showToast('✅ תכניות עודכנו בהצלחה לפי לוח התחרויות (נוכחי + הבא)!', 'success', 4500);
    } catch (e) {
      console.error('Regenerate failed:', e);
      setRegenerateToast({ mode: 'blocked', errorMessage: 'לא הצלחתי לייצר תכנית חדשה. אפשר לנסות שוב מאוחר יותר.', changedFields });
      window.dispatchEvent(new CustomEvent('plan-regen-done', { detail: { success: false, error: String(e) } }));
      showToast('⚠️ עדכון התכנית נכשל — אפשר לנסות שוב מאוחר יותר', 'error', 5000);
    }
  };
  const handleDismissRegenerateToast = () => setRegenerateToast(null);

  const handleDeleteRace = async (raceId) => {
    if (!(await askConfirm('בטוח למחוק את התחרות?', 'התחרות תימחק מלוח התחרויות.', 'מחק'))) return;

    try {
      // Capture the name BEFORE the delete so the toast can show it.
      const target = races.find(r => String(r.id) === String(raceId));
      const targetName = target?.race_name || 'תחרות';
      const response = await authFetch(`${API_BASE_URL}/races/${raceId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await loadRaces();
        // Plan-shaping change — only prompt if the deleted race was
        // close enough (21d window) to actually affect the active
        // plan. The DELETE endpoint doesn't return requires_plan_update
        // yet, so we compute it client-side from the captured race
        // date. Mirrors the same heuristic the backend uses for POST/PUT.
        const _raceDate = target?.race_date;
        const _withinWindow = (() => {
          try {
            if (!_raceDate) return false;
            const d = new Date(_raceDate + 'T00:00:00');
            if (isNaN(d.getTime())) return false;
            const today = new Date();
            today.setHours(0,0,0,0);
            const daysSinceSunday = (today.getDay()); // Sun=0
            const planStart = new Date(today);
            planStart.setDate(today.getDate() - daysSinceSunday);
            const maxImpact = new Date(planStart);
            maxImpact.setDate(planStart.getDate() + 21);
            return d >= planStart && d <= maxImpact;
          } catch { return false; }
        })();
        if (_withinWindow) {
          _openRaceImpactModal({
            summary: `מחיקת תחרות "${targetName}"`,
            raceSport: target?.sport_type || 'running',
            raceDate: _raceDate,
          });
        } else {
          showToast(`✅ התחרות "${targetName}" נמחקה.`, 'success', 3500);
        }
      }
    } catch (error) {
      console.error('Error deleting race:', error);
    }
  };

  const openEditForm = (race) => {
    setEditingRace({
      ...race,
      sport_type: race.sport_type || 'running'
    });
    setShowEditForm(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('he-IL', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Parse as LOCAL midnight. `new Date('2026-05-31')` is UTC midnight
    // per spec; in Israel (UTC+3) that's 03:00 local, so ceil() pushes
    // today's race to 1 and yesterday's race to 0 — exactly the
    // off-by-one shown in the races list. Force T00:00:00 (no Z) so
    // the parser uses the local timezone, then round instead of ceil.
    const raceDate = new Date(String(dateStr).slice(0, 10) + 'T00:00:00');
    const diff = Math.round((raceDate - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getStatusColor = (days) => {
    if (days === null || days < 0) return 'text-text-muted';
    if (days <= 7) return 'text-accent';
    return 'text-text';
  };

  const getStatusText = (days) => {
    if (days === null) return '';
    if (days < 0) return 'עבר';
    if (days === 0) return 'היום!';
    if (days === 1) return 'מחר!';
    return `עוד ${days} ימים`;
  };

  // Get all races (merged with profile goals)
  const allRaces = getAllRaces();

  // Filter races by sport type
  const filteredRaces = allRaces.filter(race => {
    const raceSport = race.sport_type || 'running';
    return raceSport === activeTab;
  });

  // Sort races: upcoming first (by date), then past
  const sortedRaces = [...filteredRaces].sort((a, b) => {
    const daysA = getDaysUntil(a.race_date);
    const daysB = getDaysUntil(b.race_date);
    
    // Past races go to the end
    if (daysA < 0 && daysB >= 0) return 1;
    if (daysB < 0 && daysA >= 0) return -1;
    
    // Sort by date
    return new Date(a.race_date) - new Date(b.race_date);
  });

  // Count races by sport type
  const runningCount = allRaces.filter(r => (r.sport_type || 'running') === 'running').length;
  const cyclingCount = allRaces.filter(r => r.sport_type === 'cycling').length;

  // Open add form with correct sport type
  const openAddForm = () => {
    setNewRace({
      race_name: '',
      race_date: '',
      distance: activeTab === 'cycling' ? '70 ק"מ' : '10K',
      target_time: '',
      is_goal_race: false,
      sport_type: activeTab
    });
    setShowAddForm(true);
  };

  return (
    <div className="min-h-screen bg-bg overflow-x-hidden">
    <div className="max-w-lg mx-auto" dir="rtl">
      {/* Race-change regenerate toast — fixed at bottom so it overlays
          whatever scrolled list of races the user is on. Same shape +
          modes as the Profile.jsx toast; same plan-regen-start/done
          events drive the global App.js banner. */}
      {raceImpactModal && (() => {
        const m = raceImpactModal;
        const _hasQuota = (typeof m.remaining === 'number' && typeof m.cost === 'number');
        const _insufficient = _hasQuota && m.remaining < m.cost;
        const _sportLabel = (m.target_sports || []).length === 0
          ? ''
          : m.target_sports.length === 2 ? 'ריצה + רכיבה'
          : m.target_sports[0] === 'cycling' ? 'רכיבה' : 'ריצה';
        const _dateLabel = m.raceDate ? new Date(m.raceDate + 'T00:00:00').toLocaleDateString('he-IL') : '';
        return (
          <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/70 p-3 sm:p-4">
            <div className="max-w-lg w-full rounded-[16px] bg-surface border border-border p-4 sm:p-5">
              <p className="text-text text-base font-semibold mb-2">
                {m.isGoalRace ? 'מרוץ מטרה — עדכון תוכנית' : 'זיהינו תחרות קרובה!'}
              </p>
              <p className="text-text-body text-sm mb-2">{m.summary}{_dateLabel ? ` · ${_dateLabel}` : ''}</p>
              <p className="text-text-body text-sm leading-snug mb-3">
                {m.isGoalRace
                  ? `מרוץ מטרה משנה את כל עיצוב התוכנית — הסקלטון מתאים עצמו למרחק ולתאריך. כדי לשקף את השינוי, המערכת תייצר ${m.cost ?? '—'} תוכניות ${_sportLabel ? `ל${_sportLabel}` : ''} מחדש.`
                  : `מרוץ האימון ישולב בתוכנית — שבוע המרוץ וההתאוששות יתעדכנו סביבו. עם זאת, סוג תוכנית האימונים (5K / 10K / חצי / מרתון) לא ישתנה. כדי לבנות תוכנית ייעודית למרוץ זה, סמנו אותו כ"תחרות מטרה". המערכת תייצר ${m.cost ?? '—'} תוכניות ${_sportLabel ? `ל${_sportLabel}` : ''} מחדש.`
                }
              </p>
              {_hasQuota && (
                <div className="rounded-lg bg-pressed border border-border p-2.5 mb-3 text-xs leading-snug">
                  <p className="text-text-body">
                    קריאות נדרשות: <span className="font-semibold text-text-body">{m.cost}</span>
                    {' · '}
                    יתרה: <span className="font-semibold text-text-body">{m.remaining}</span>
                    {typeof m.daily_limit === 'number' ? <span className="text-text-muted"> מתוך {m.daily_limit}</span> : null}
                  </p>
                  {_insufficient ? (
                    <p className="text-text-body mt-1.5">
                      אין מספיק קריאות פנויות (נדרשות {m.cost}, נשארו {m.remaining}). העדכון יתאפשר מחר.
                    </p>
                  ) : (
                    <p className="text-text-muted mt-1.5">
                      לאחר העדכון יישארו לך {Math.max(0, m.remaining - m.cost)} קריאות.
                    </p>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={_confirmRaceImpactUpdate}
                  disabled={_insufficient}
                  className={
                    'flex-1 min-h-[48px] px-3 rounded-full text-sm btn-primary transition-colors'
                  }
                >
                  {_insufficient ? 'לא ניתן עכשיו' : `עדכן תוכנית (כ-${m.cost === 2 ? '2.5' : '5'} דקות)`}
                </button>
                <button
                  onClick={() => setRaceImpactModal(null)}
                  className="min-h-[48px] px-4 rounded-full bg-transparent border border-border text-text-body font-semibold text-sm"
                >
                  לא עכשיו
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {regenerateToast && (
        <div className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4">
          <div className="max-w-lg mx-auto rounded-[16px] bg-surface border border-border p-4 sm:p-5">
            {regenerateToast.mode === 'ask' && (
              <>
                <p className="text-text text-sm font-semibold mb-2">
                  התחרויות עודכנו
                </p>
                {regenerateToast.changedFields && regenerateToast.changedFields.length > 0 && (
                  <p className="text-text-body text-sm leading-snug mb-2">
                    <span className="font-semibold">פעולה: </span>
                    {regenerateToast.changedFields.join(', ')}
                  </p>
                )}
                <p className="text-text-body text-sm leading-snug mb-2">
                  זה משנה את לוח התחרויות שלך — רוצה שהמאמן יבנה לך תוכנית שבועית חדשה שתואמת את השינוי?
                </p>
                <p className="text-text-body text-[11px] leading-snug mb-3">
                  שים לב: יצירת תוכנית מחדש תשתמש בתוך מכסת העדכונים היומית שלך.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleConfirmRegenerate}
                    className="flex-1 min-h-[48px] px-3 rounded-full btn-primary text-sm"
                  >
                    כן, תייצר לי תוכנית מחדש
                  </button>
                  <button
                    onClick={handleDismissRegenerateToast}
                    className="min-h-[48px] px-4 rounded-full bg-transparent border border-border text-text-body font-semibold text-sm"
                  >
                    לא כרגע
                  </button>
                </div>
              </>
            )}
            {regenerateToast.mode === 'blocked' && (
              <>
                <p className="text-text text-sm font-semibold mb-2">
                  השינוי נשמר
                </p>
                <p className="text-text-body text-sm leading-snug mb-3">
                  {regenerateToast.errorMessage || (
                    <>
                      ניתן לייצר תוכנית מחדש פעם אחת ביום
                      {regenerateToast.hoursRemaining ? (
                        <span> (אפשר שוב בעוד כ-{regenerateToast.hoursRemaining} שעות).</span>
                      ) : '.'}
                      {' '}השינויים ישתקפו אוטומטית בשבוע הבא.
                    </>
                  )}
                </p>
                <button
                  onClick={handleDismissRegenerateToast}
                  className="w-full min-h-[48px] px-3 rounded-full bg-transparent border border-border text-text-body font-semibold text-sm"
                >
                  הבנתי
                </button>
              </>
            )}
            {regenerateToast.mode === 'generating' && (
              <div className="flex items-center gap-3 py-1">
                <div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                <p className="text-text text-sm">בונה לך תוכנית חדשה לפי לוח התחרויות המעודכן…</p>
              </div>
            )}
            {regenerateToast.mode === 'done' && (
              <div className="flex items-center gap-3 py-1">
                <CheckCircle size={22} weight="bold" className="text-accent shrink-0" aria-hidden="true" />
                <p className="text-text text-sm">התוכנית עודכנה — בדוק במסך התכנית השבועית.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-20 bg-bg border-b border-border">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onBack}
              aria-label="חזרה"
              className="w-11 h-11 shrink-0 rounded-full border border-border flex items-center justify-center text-text-body"
            >
              <ArrowRight size={20} weight="bold" aria-hidden="true" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-[28px] font-extrabold leading-tight text-text truncate">התחרויות שלי</h1>
              <p className="text-[14px] text-text-muted truncate">לוח התחרויות שלך</p>
            </div>
            {activeTab === 'pbs' ? (
              <button
                onClick={() => setShowAddResult(true)}
                className="shrink-0 min-h-[44px] px-4 rounded-full btn-primary text-[14px]"
              >
                <span className="min-[380px]:hidden">+ תוצאה</span>
                <span className="hidden min-[380px]:inline">+ הוסף תוצאה</span>
              </button>
            ) : (
              <button
                onClick={openAddForm}
                className="shrink-0 min-h-[44px] px-4 rounded-full btn-primary text-[14px]"
              >
                <span className="min-[380px]:hidden">+ {activeTab === 'cycling' ? 'תחרות' : 'מרוץ'}</span>
                <span className="hidden min-[380px]:inline">+ הוסף {activeTab === 'cycling' ? 'תחרות רכיבה' : 'מרוץ'}</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* 🚴🏃 Sport Type Tabs */}
      <div className="container mx-auto px-4 py-4">
        <div className="max-w-lg mx-auto">
          <div role="tablist" className="flex gap-2 p-1 rounded-[12px] border border-border">
            <button
              onClick={() => setActiveTab('running')}
              role="tab" className={`flex-1 min-h-[44px] px-3 rounded-[10px] font-medium transition-all flex items-center justify-center gap-1.5 text-[15px] ${
                activeTab === 'running'
                  ? 'bg-accent text-on-accent font-bold'
                  : 'text-text-body'
              }`}
            >
              <span>מרוצים</span>
              {runningCount > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === 'running' ? 'bg-bg text-text' : 'bg-pressed'
                }`}>{runningCount}</span>
              )}
            </button>
            {CYCLING_ENABLED && (
              <button
                onClick={() => setActiveTab('cycling')}
                role="tab" className={`flex-1 min-h-[44px] px-3 rounded-[10px] font-medium transition-all flex items-center justify-center gap-1.5 text-[15px] ${
                  activeTab === 'cycling'
                    ? 'bg-accent text-on-accent font-bold'
                    : 'text-text-body'
                }`}
              >
                <span>רכיבה</span>
                {cyclingCount > 0 && (
                  <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                    activeTab === 'cycling' ? 'bg-bg text-text' : 'bg-pressed'
                  }`}>{cyclingCount}</span>
                )}
              </button>
            )}
            <button
              onClick={() => setActiveTab('pbs')}
              role="tab" className={`flex-1 min-h-[44px] px-3 rounded-[10px] font-medium transition-all flex items-center justify-center gap-1.5 text-[15px] ${
                activeTab === 'pbs'
                  ? 'bg-accent text-on-accent font-bold'
                  : 'text-text-body'
              }`}
            >
              <span>ביצועים</span>
              {raceResults.length > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === 'pbs' ? 'bg-bg text-text' : 'bg-pressed'
                }`}>{raceResults.length}</span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-2">
        <div className="max-w-lg mx-auto">
          
          {/* Loading */}
          {loading && (
            <div className="text-center py-12">
              <div className="w-12 h-12 mx-auto rounded-2xl bg-pressed flex items-center justify-center text-text-muted mb-4 animate-pulse">
                <Flag size={24} weight="bold" aria-hidden="true" />
              </div>
              <p className="text-text-muted">טוען...</p>
            </div>
          )}

          {/* Personal Bests Tab */}
          {!loading && activeTab === 'pbs' && (
            <div>
              {raceResults.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-pressed flex items-center justify-center text-accent mb-4">
                    <Trophy size={30} weight="bold" aria-hidden="true" />
                  </div>
                  <h2 className="text-xl font-bold text-text mb-2">אין ביצועים עדיין</h2>
                  <p className="text-text-muted mb-6">הוסף תוצאות ממרוצים ואימונים איכותיים — המערכת תשתמש בהן לתכנון אימונים מדויק</p>
                  <button
                    onClick={() => setShowAddResult(true)}
                    className="min-h-[48px] px-6 rounded-full btn-primary"
                  >
                    + הוסף תוצאה ראשונה
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Best per distance summary */}
                  {(() => {
                    const cats = ['5k', '10k', 'half', 'marathon'];
                    const labels = { '5k': '5 ק"מ', '10k': '10 ק"מ', 'half': 'חצי מרתון', 'marathon': 'מרתון' };
                    const bests = {};
                    raceResults.forEach(r => {
                      if (r.is_pb) bests[r.distance_category] = r;
                    });
                    const hasBests = cats.some(c => bests[c]);
                    if (!hasBests) return null;
                    return (
                      <div className="p-4 rounded-[16px] bg-surface border border-border mb-4">
                        <h3 className="text-[13px] font-semibold text-text-muted mb-3">ביצועים אחרונים (6 חודשים)</h3>
                        <div className="grid grid-cols-2 gap-2">
                          {cats.map(cat => bests[cat] ? (
                            <div key={cat} className="bg-bg rounded-[12px] border border-border p-3 min-w-0">
                              <p className="text-xs text-text-muted">{labels[cat]}</p>
                              <p className="num-display text-text text-[26px] leading-none mt-1 truncate tabular-nums">{bests[cat].finish_time_display}</p>
                              <p className="text-xs text-text-muted truncate">{bests[cat].race_name}</p>
                              <p className="text-xs text-text-muted truncate">{bests[cat].race_date}</p>
                            </div>
                          ) : null)}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Computed Training Paces */}
                  {(() => {
                    // toEquiv10k: כמה להוסיף לקצב מרוץ כדי לקבל קצב 10K שקיל
                    // (5K מהיר ב-5 שנ' מ-10K; חצי איטי ב-12 שנ' מ-10K)
                    const toEquiv10k = { '5k': 5, '10k': 0, 'half': -12, 'marathon': -35 };
                    // tempoOffsets: כמה להוסיף לקצב מרוץ YEF כדי לקבל קצב טמפו
                    const tempoOffsets = { '5k': [10, 20], '10k': [5, 15], 'half': [-10, 0], 'marathon': [-20, -10] };
                    const distKm = { '5k': 5, '10k': 10, 'half': 21.1, 'marathon': 42.2 };
                    const distLabels = { '5k': '5 ק"מ', '10k': '10 ק"מ', 'half': 'חצי מרתון', 'marathon': 'מרתון' };

                    const parseTimeSec = (t) => {
                      if (!t) return null;
                      const p = t.split(':').map(Number);
                      if (p.length === 3) return p[0] * 3600 + p[1] * 60 + p[2];
                      if (p.length === 2) return p[0] * 60 + p[1];
                      return null;
                    };
                    const fmtPace = (sec) => `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, '0')}`;
                    const fmtTime = (sec) => {
                      const h = Math.floor(sec / 3600);
                      const m = Math.floor((sec % 3600) / 60);
                      const s = Math.floor(sec % 60);
                      return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
                    };

                    // מצא תוצאה הטובה ביותר לפי 10K שקיל נמוך
                    let best10kEq = null;
                    let bestCat = null;
                    let bestResultName = null;
                    for (const r of raceResults) {
                      if (!r.is_pb) continue;
                      const dk = distKm[r.distance_category];
                      if (!dk) continue;
                      const totalSec = parseTimeSec(r.finish_time_display);
                      if (!totalSec) continue;
                      const paceSec = totalSec / dk;
                      const eq = paceSec + (toEquiv10k[r.distance_category] || 0);
                      if (best10kEq === null || eq < best10kEq) {
                        best10kEq = eq;
                        bestCat = r.distance_category;
                        bestResultName = r.finish_time_display;
                      }
                    }
                    if (best10kEq === null) return null;

                    // חשב שקילויות מרחקים
                    const equivs = Object.entries(distKm).map(([cat, dk]) => {
                      const paceSec = best10kEq - (toEquiv10k[cat] || 0);
                      const timeSec = paceSec * dk;
                      return { cat, pace: fmtPace(paceSec), time: fmtTime(timeSec), isActual: cat === bestCat };
                    });

                    // קצב טמפו לפי מרחק יעד (מהפרופיל) או לפי ביצוע הטוב ביותר
                    const goalDistRaw = (runningProfile?.goal_distance || '').toLowerCase();
                    const goalCatMap = { '5k': '5k', '10k': '10k', 'חצי מרתון': 'half', 'half': 'half', 'half_marathon': 'half', 'half marathon': 'half', 'מרתון': 'marathon', 'marathon': 'marathon' };
                    const tempoCat = goalCatMap[goalDistRaw] || bestCat;
                    // קצב מרוץ לפי מרחק יעד (ממיר דרך 10K שקיל)
                    const goalRacePaceSec = best10kEq - (toEquiv10k[tempoCat] || 0);
                    const [tempoLo, tempoHi] = tempoOffsets[tempoCat] || [0, 15];
                    // ריצה קלה = 10K שקיל + 60-90 שנ'
                    const easyLo = best10kEq + 60;
                    const easyHi = best10kEq + 90;

                    return (
                      <div className="p-4 rounded-[16px] bg-surface border border-border mb-1">
                        <h3 className="text-[13px] font-semibold text-text-muted mb-3">קצבי אימון מחושבים</h3>
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <div className="bg-bg rounded-[12px] border border-border p-3">
                            <p className="text-xs text-text-muted mb-1">טמפו / LT</p>
                            <p className="text-text font-mono font-bold text-sm">{fmtPace(goalRacePaceSec + tempoLo)}–{fmtPace(goalRacePaceSec + tempoHi)}</p>
                            <p className="text-xs text-text-muted">דק'/ק"מ</p>
                          </div>
                          <div className="bg-bg rounded-[12px] border border-border p-3">
                            <p className="text-xs text-text-muted mb-1">ריצה קלה</p>
                            <p className="text-text font-mono font-bold text-sm">{fmtPace(easyLo)}–{fmtPace(easyHi)}</p>
                            <p className="text-xs text-text-muted">דק'/ק"מ</p>
                          </div>
                        </div>
                        <div className="border-t border-border pt-3">
                          <p className="text-xs text-text-muted mb-2">שקילות מרחקים (לפי {distLabels[bestCat]} {bestResultName})</p>
                          <div className="space-y-1">
                            {equivs.map(({ cat, pace, time, isActual }) => (
                              <div key={cat} className="flex items-center justify-between text-xs">
                                <span className={isActual ? 'text-accent font-semibold' : 'text-text-muted'}>
                                  {distLabels[cat]}{isActual ? ' ✓' : ''}
                                </span>
                                <span className={`tabular-nums ${isActual ? 'text-accent' : 'text-text'}`}>
                                  {time} · {pace}/ק"מ
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* All results */}
                  <h3 className="text-text-muted text-sm font-medium px-1">כל התוצאות</h3>
                  {raceResults.map(r => {
                    const catLabels = { '5k': '5 ק"מ', '10k': '10 ק"מ', 'half': 'חצי מרתון', 'marathon': 'מרתון', 'other': 'אחר' };
                    return (
                      <div key={r.id} className={`p-4 rounded-2xl border transition-all ${
                        r.is_pb
                          ? 'bg-surface border-accent'
                          : 'bg-surface border-border'
                      }`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-text font-semibold">{r.race_name}</span>
                              {r.is_pb && <span className="px-2 py-0.5 rounded-full border border-accent text-accent text-xs font-semibold">שיא</span>}
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-sm text-text-muted flex-wrap">
                              <span>{catLabels[r.distance_category] || r.distance_category}</span>
                              <span className="text-text font-bold tabular-nums">{r.finish_time_display}</span>
                              <span>{r.race_date}</span>
                            </div>
                            {r.notes && <p className="text-xs text-text-muted mt-1">{r.notes}</p>}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeleteResult(r.id)}
                            aria-label="מחק תוצאה"
                            className="w-11 h-11 shrink-0 rounded-full border border-border flex items-center justify-center text-text-muted"
                          >
                            <Trash size={18} weight="bold" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Empty State - races */}
          {!loading && activeTab !== 'pbs' && sortedRaces.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-pressed flex items-center justify-center text-accent mb-4">
                <FlagCheckered size={30} weight="bold" aria-hidden="true" />
              </div>
              <h2 className="text-xl font-bold text-text mb-2">
                {activeTab === 'cycling' ? 'אין תחרויות רכיבה עדיין' : 'אין מרוצי ריצה עדיין'}
              </h2>
              <p className="text-text-muted mb-6">
                {activeTab === 'cycling'
                  ? 'הוסף תחרויות רכיבה כדי לעקוב אחרי היעדים שלך'
                  : 'הוסף מרוצים כדי לעקוב אחרי ההתקדמות שלך'
                }
              </p>
              <button
                onClick={openAddForm}
                className="min-h-[48px] px-6 rounded-full btn-primary"
              >
                + הוסף {activeTab === 'cycling' ? 'תחרות רכיבה' : 'מרוץ'} ראשון
              </button>
            </div>
          )}

          {/* Races List */}
          {!loading && activeTab !== 'pbs' && sortedRaces.length > 0 && (
            <div className="space-y-3">
              {sortedRaces.map((race) => {
                const daysUntil = getDaysUntil(race.race_date);
                const isPast = daysUntil !== null && daysUntil < 0;
                const isCycling = race.sport_type === 'cycling';
                const isFromProfile = race.from_profile;
                
                return (
                  <div
                    key={race.id}
                    data-testid="race-card"
                    className={`rounded-[16px] border transition-all ${
                      isPast
                        ? 'bg-surface border-border opacity-70'
                        : race.is_goal_race
                          ? 'bg-surface border-accent'
                          : 'bg-surface border-border'
                    }`}
                  >
                    <div className="p-4">
                      <div className="flex items-start gap-3">
                        {/* Icon */}
                        <div className={`w-12 h-12 rounded-[12px] bg-pressed flex items-center justify-center shrink-0 ${
                          !isPast && race.is_goal_race ? 'text-accent' : 'text-text-body'}`} aria-hidden="true">
                          {isPast ? <Check size={22} weight="bold" /> : race.is_goal_race ? <Target size={22} weight="bold" />
                            : isCycling ? <Bicycle size={22} weight="bold" /> : <PersonSimpleRun size={22} weight="bold" />}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-text text-[17px] font-bold leading-tight">{race.race_name}</h3>
                            {!isPast && (
                              race.is_goal_race ? (
                                <IntensityBadge
                                  label={isCycling ? 'אירוע יעד' : 'מרוץ מטרה'}
                                  color="amber"
                                  size="sm"
                                />
                              ) : (
                                <IntensityBadge label="אימון" color="cyan" size="sm" />
                              )
                            )}
                            {isFromProfile && (
                              <IntensityBadge label="מהפרופיל" color="purple" size="sm" />
                            )}
                          </div>

                          <div className="flex items-center gap-x-3 gap-y-1 mt-1.5 text-[14px] text-text-muted flex-wrap">
                            <span>{formatDate(race.race_date)}</span>
                            {race.distance && <span className="font-semibold text-text-body">{race.distance}</span>}
                            {race.target_time && (
                              <span className="font-semibold text-accent tabular-nums whitespace-nowrap">יעד {race.target_time}</span>
                            )}
                          </div>
                        </div>

                      </div>

                      {/* Big countdown + progress bar (upcoming races only).
                          Bar fill is a soft 0→100% projection over the last
                          90 days before race day, so a race 60 days out shows
                          ~33% and race day shows 100%. Visual cue, not an
                          exact measure of training progress. */}
                      {!isPast && daysUntil !== null && (
                        <div className="mt-3 pt-3 border-t border-border flex items-center gap-3">
                          <div className="text-center shrink-0 min-w-[48px]">
                            <p className={`num-display text-[36px] leading-none ${getStatusColor(daysUntil)}`}>
                              {daysUntil}
                            </p>
                            <p className="text-text-muted text-xs mt-1">ימים</p>
                          </div>
                          <div className="flex-1">
                            <div className="bg-bar rounded-full h-1.5 overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${
                                  race.is_goal_race ? 'bg-accent' : 'bg-text-body'
                                }`}
                                style={{
                                  width: `${Math.max(2, Math.min(100, ((90 - daysUntil) / 90) * 100))}%`,
                                }}
                              />
                            </div>
                            <p className="text-[11px] text-text-muted mt-1.5">
                              {getStatusText(daysUntil)}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* AI Notes — reusable CoachNote (info variant for race strategy) */}
                      <CoachNote variant="info" title="הערת מאמן למרוץ" icon="" className="mt-3">
                        {race.ai_notes}
                      </CoachNote>

                      {/* Edit / delete — always an explicit tap by the athlete (delete asks first) */}
                      {!isPast && !isFromProfile && (
                        <div className="mt-3 flex gap-2 justify-end">
                          <button type="button" onClick={() => openEditForm(race)} data-testid="race-edit"
                                  className="min-h-[44px] px-4 rounded-full bg-transparent border border-border text-text text-[14px] font-semibold">
                            עריכת המרוץ
                          </button>
                          <button type="button" onClick={() => handleDeleteRace(race.id)} data-testid="race-delete"
                                  className="min-h-[44px] px-4 rounded-full bg-transparent border border-border text-text-muted text-[14px] font-semibold">
                            מחק
                          </button>
                        </div>
                      )}
                      {isFromProfile && (
                        <p className="mt-3 text-[13px] text-text-muted">ערוך בפרופיל</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </div>
      </main>

      {/* Add Race Modal */}
      {showAddForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="w-full max-w-md max-h-[92vh] overflow-y-auto bg-surface rounded-[16px] p-5 border border-border">
            <h2 className="text-xl font-bold text-text mb-4">הוסף תחרות חדשה</h2>
            
            {/* 🚴🏃 Sport Type Selector */}
            <div className="mb-4">
              <label className="block text-sm text-text-muted mb-2">סוג ספורט</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setNewRace({ ...newRace, sport_type: 'running', distance: '10K' })}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                    newRace.sport_type === 'running'
                      ? 'bg-accent text-on-accent'
                      : 'bg-transparent border border-border text-text-body'
                  }`}
                >
                  <span>ריצה</span>
                </button>
                {CYCLING_ENABLED && (
                  <button
                    type="button"
                    onClick={() => setNewRace({ ...newRace, sport_type: 'cycling', distance: '70 ק"מ' })}
                    className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                      newRace.sport_type === 'cycling'
                        ? 'bg-accent text-on-accent'
                        : 'bg-transparent border border-border text-text-body'
                    }`}
                  >
                    <span>רכיבה</span>
                  </button>
                )}
              </div>
            </div>

            {/* Race Name */}
            <div className="mb-4">
              <label className="block text-sm text-text-muted mb-1">
                {newRace.sport_type === 'cycling' ? 'שם האירוע *' : 'שם המרוץ *'}
              </label>
              <input
                type="text"
                value={newRace.race_name}
                onChange={(e) => setNewRace({ ...newRace, race_name: e.target.value })}
                placeholder={newRace.sport_type === 'cycling' 
                  ? 'לדוגמה: Gran Fondo ישראל' 
                  : 'לדוגמה: מרתון תל אביב'
                }
                className="w-full px-4 py-3 rounded-xl bg-bg border border-border text-text placeholder-text-muted focus:outline-none focus:border-accent"
              />
            </div>

            {/* Race Date */}
            <div className="mb-4">
              <label className="block text-sm text-text-muted mb-1">תאריך *</label>
              <input
                type="date"
                value={newRace.race_date}
                onChange={(e) => setNewRace({ ...newRace, race_date: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-bg border border-border text-text focus:outline-none focus:border-accent"
              />
            </div>

            {/* Distance */}
            <div className="mb-4">
              <label className="block text-sm text-text-muted mb-1">מרחק</label>
              <select
                value={newRace.distance}
                onChange={(e) => setNewRace({ ...newRace, distance: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-bg border border-border text-text focus:outline-none focus:border-accent"
              >
                {getDistanceOptions(newRace.sport_type).map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>

            {/* Target Time */}
            <div className="mb-4">
              <label className="block text-sm text-text-muted mb-1">זמן יעד *</label>
              <button
                type="button"
                onClick={() => setAddRacePickerOpen(true)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-bg border border-border hover:border-border transition-colors"
              >
                <span className="text-text-muted text-sm">בחר זמן יעד</span>
                {newRace.target_time
                  ? <span className="text-accent font-bold tabular-nums">{newRace.target_time}</span>
                  : <span className="text-text-muted text-sm">הזן זמן ←</span>}
              </button>
            </div>
            {/* Race type (Goal vs Tune-up) is now picked in a modal AFTER
                clicking "הוסף תחרות" — see RaceTypePickerModal. The legacy
                inline checkbox was removed to make the choice deliberate. */}

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowAddForm(false)}
                className="flex-1 min-h-[48px] rounded-full bg-transparent border border-border text-text-body font-semibold"
              >
                ביטול
              </button>
              <button
                onClick={handleAddRace}
                disabled={saving}
                className="flex-1 min-h-[48px] rounded-full btn-primary transition-colors"
              >
                {saving ? 'שומר...' : 'הוסף תחרות'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Race Modal */}
      {showEditForm && editingRace && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="w-full max-w-md max-h-[92vh] overflow-y-auto bg-surface rounded-[16px] p-5 border border-border">
            <h2 className="text-xl font-bold text-text mb-4">עריכת תחרות</h2>
            
            {/* 🚴🏃 Sport Type Selector */}
            <div className="mb-4">
              <label className="block text-sm text-text-muted mb-2">סוג ספורט</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setEditingRace({ ...editingRace, sport_type: 'running', distance: editingRace.distance || '10K' })}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                    editingRace.sport_type === 'running'
                      ? 'bg-accent text-on-accent'
                      : 'bg-transparent border border-border text-text-body'
                  }`}
                >
                  <span>ריצה</span>
                </button>
                {CYCLING_ENABLED && (
                  <button
                    type="button"
                    onClick={() => setEditingRace({ ...editingRace, sport_type: 'cycling', distance: editingRace.distance || '70 ק"מ' })}
                    className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                      editingRace.sport_type === 'cycling'
                        ? 'bg-accent text-on-accent'
                        : 'bg-transparent border border-border text-text-body'
                    }`}
                  >
                    <span>רכיבה</span>
                  </button>
                )}
              </div>
            </div>

            {/* Race Name */}
            <div className="mb-4">
              <label className="block text-sm text-text-muted mb-1">
                {editingRace.sport_type === 'cycling' ? 'שם האירוע *' : 'שם המרוץ *'}
              </label>
              <input
                type="text"
                value={editingRace.race_name}
                onChange={(e) => setEditingRace({ ...editingRace, race_name: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-bg border border-border text-text placeholder-text-muted focus:outline-none focus:border-accent"
              />
            </div>

            {/* Race Date */}
            <div className="mb-4">
              <label className="block text-sm text-text-muted mb-1">תאריך *</label>
              <input
                type="date"
                value={editingRace.race_date}
                onChange={(e) => setEditingRace({ ...editingRace, race_date: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-bg border border-border text-text focus:outline-none focus:border-accent"
              />
            </div>

            {/* Distance */}
            <div className="mb-4">
              <label className="block text-sm text-text-muted mb-1">מרחק</label>
              <select
                value={editingRace.distance}
                onChange={(e) => setEditingRace({ ...editingRace, distance: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-bg border border-border text-text focus:outline-none focus:border-accent"
              >
                {getDistanceOptions(editingRace.sport_type).map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>

            {/* Target Time */}
            <div className="mb-4">
              <label className="block text-sm text-text-muted mb-1">זמן יעד *</label>
              <button
                type="button"
                onClick={() => setEditRacePickerOpen(true)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-bg border border-border hover:border-border transition-colors"
              >
                <span className="text-text-muted text-sm">בחר זמן יעד</span>
                {editingRace.target_time
                  ? <span className="text-accent font-bold tabular-nums">{editingRace.target_time}</span>
                  : <span className="text-text-muted text-sm">הזן זמן ←</span>}
              </button>
            </div>
            {/* Race type chosen via modal after clicking "שמור" — see
                RaceTypePickerModal. */}

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => { setShowEditForm(false); setEditingRace(null); }}
                className="flex-1 min-h-[48px] rounded-full bg-transparent border border-border text-text-body font-semibold"
              >
                ביטול
              </button>
              <button
                onClick={handleEditRace}
                disabled={saving}
                className="flex-1 min-h-[48px] rounded-full btn-primary transition-colors"
              >
                {saving ? 'שומר...' : 'שמור שינויים'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Add Race Result Modal */}
      {showAddResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="w-full max-w-md max-h-[92vh] overflow-y-auto bg-surface rounded-[16px] p-5 border border-border">
            <h2 className="text-xl font-bold text-text mb-4">הוסף תוצאת מרוץ / אימון</h2>

            <div className="mb-4">
              <label className="block text-sm text-text-muted mb-1">מרחק *</label>
              <select
                value={newResult.distance_category}
                onChange={(e) => setNewResult({ ...newResult, distance_category: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-bg border border-border text-text focus:outline-none focus:border-accent"
              >
                <option value="5k">5 ק"מ</option>
                <option value="10k">10 ק"מ</option>
                <option value="half">חצי מרתון</option>
                <option value="marathon">מרתון</option>
                <option value="other">אחר</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm text-text-muted mb-1">שם המרוץ / האימון *</label>
              <input
                type="text"
                value={newResult.race_name}
                onChange={(e) => setNewResult({ ...newResult, race_name: e.target.value })}
                placeholder="לדוגמה: מרוץ אור יהודה / טמפו 5K"
                className="w-full px-4 py-3 rounded-xl bg-bg border border-border text-text placeholder-text-muted focus:outline-none focus:border-accent"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm text-text-muted mb-1">תאריך *</label>
              <input
                type="date"
                value={newResult.race_date}
                onChange={(e) => setNewResult({ ...newResult, race_date: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-bg border border-border text-text focus:outline-none focus:border-accent"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm text-text-muted mb-2">זמן סיום *</label>
              <button
                type="button"
                onClick={() => setResultPickerOpen(true)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-bg border border-border hover:border-border transition-colors"
              >
                <span className="text-text-muted text-sm">גלול לבחור זמן</span>
                {newResult.finish_time_display ? (
                  <span className="text-accent font-bold text-lg tabular-nums">{newResult.finish_time_display}</span>
                ) : (
                  <span className="text-text-muted text-sm font-medium">הזן זמן ←</span>
                )}
              </button>
            </div>


            <div className="mb-6">
              <label className="block text-sm text-text-muted mb-1">הערות (אופציונלי)</label>
              <input
                type="text"
                value={newResult.notes}
                onChange={(e) => setNewResult({ ...newResult, notes: e.target.value })}
                placeholder="לדוגמה: מזג אוויר חם, מסלול הררי"
                className="w-full px-4 py-3 rounded-xl bg-bg border border-border text-text placeholder-text-muted focus:outline-none focus:border-accent"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowAddResult(false)}
                className="flex-1 min-h-[48px] rounded-full bg-transparent border border-border text-text-body font-semibold"
              >
                ביטול
              </button>
              <button
                onClick={handleAddResult}
                disabled={savingResult}
                className="flex-1 min-h-[48px] rounded-full btn-primary transition-colors"
              >
                {savingResult ? 'שומר...' : 'שמור תוצאה'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* TimePickers — rendered at root level so z-index works above modals */}
      {addRacePickerOpen && (
        <TimePicker
          label={`זמן יעד — ${newRace.distance || ''}`}
          value={newRace.target_time}
          showHours={newRace.distance !== '5K'}
          distanceKm={distanceKmFrom(newRace.distance)}
          onChange={(v) => setNewRace({ ...newRace, target_time: v })}
          onClose={() => setAddRacePickerOpen(false)}
        />
      )}
      {editRacePickerOpen && editingRace && (
        <TimePicker
          label={`זמן יעד — ${editingRace.distance || ''}`}
          value={editingRace.target_time || ''}
          showHours={editingRace.distance !== '5K'}
          distanceKm={distanceKmFrom(editingRace.distance)}
          onChange={(v) => setEditingRace({ ...editingRace, target_time: v })}
          onClose={() => setEditRacePickerOpen(false)}
        />
      )}
      {resultPickerOpen && (
        <TimePicker
          label="הוסף תוצאה"
          value={newResult.finish_time_display}
          showHours={newResult.distance_category !== '5k'}
          distances={[
            { label: '5K',     km: 5    },
            { label: '10K',    km: 10   },
            { label: '21.1',   km: 21.1 },
            { label: '42.2',   km: 42.2 },
          ]}
          distanceKm={
            newResult.distance_category === '5k' ? 5
            : newResult.distance_category === '10k' ? 10
            : newResult.distance_category === 'half' ? 21.1
            : newResult.distance_category === 'marathon' ? 42.2
            : null}
          onDistanceChange={(km) => {
            const cat = km === 5 ? '5k' : km === 10 ? '10k' : km === 21.1 ? 'half' : 'marathon';
            setNewResult(r => ({ ...r, distance_category: cat }));
          }}
          onChange={(v) => setNewResult({ ...newResult, finish_time_display: v })}
          onClose={() => setResultPickerOpen(false)}
        />
      )}

      {guardrailState && (
        <GoalGuardrailModal
          result={guardrailState.result}
          onAdoptSuggestion={() => {
            // Move the user to the suggested (safer) distance and clear the
            // target time — the AI will follow up to ask for a new goal time.
            const suggested = guardrailState.result.suggestedDistance;
            if (guardrailState.mode === 'add') {
              setNewRace((p) => ({ ...p, distance: suggested, target_time: '' }));
            } else {
              setEditingRace((p) => ({ ...p, distance: suggested, target_time: '' }));
            }
            setGuardrailState(null);
          }}
          onContinueAnyway={async () => {
            const mode = guardrailState.mode;
            setGuardrailState(null);
            // Preserve the user's goal/tune-up pick from the type picker
            // (the guardrail now fires for both, so we can no longer assume
            // is_goal_race=true here).
            const isGoal = mode === 'add'
              ? !!newRace.is_goal_race
              : !!editingRace?.is_goal_race;
            if (mode === 'add') await persistNewRace(true, isGoal);
            else await persistEditRace(true, isGoal);
          }}
          onClose={() => setGuardrailState(null)}
        />
      )}

      <ProfileDialog dialog={confirmDialog} onClose={closeConfirm} />

      {raceTypePicker && (
        <RaceTypePickerModal
          raceName={raceTypePicker.mode === 'add' ? newRace.race_name : editingRace?.race_name}
          raceDate={raceTypePicker.mode === 'add' ? newRace.race_date : editingRace?.race_date}
          onSelect={(isGoalRace) => {
            if (raceTypePicker.mode === 'add') {
              finalizeAddAfterTypePick(isGoalRace);
            } else {
              finalizeEditAfterTypePick(isGoalRace);
            }
          }}
        />
      )}
    </div>
    </div>
  );
}

export default Races;
